import React, { Suspense } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { NumberParam, useQueryParam } from 'use-query-params';
import { Box, Button, Card, CardHeader, Container, IconButton, Link, Tooltip } from '@mui/material';
import { Add, Delete, DirectionsBoat, Edit, EmojiTransportation, WarningAmber } from '@mui/icons-material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { typePhone } from 'components/mui/Formatters';
import { UserModel } from 'models/UserModel';
import { YardModel } from 'models/YardModel';
import { CompanyType } from 'models/CompanyModel';
import { VesselModel } from 'models/VesselModel';
import useUserModals from 'modules/users/hooks/useUserModals';
import userListFamily from 'recoil/atoms/userListFamily';
import auth from 'recoil/atoms/auth';


const UsersInner = () => {
    const [companyId] = useQueryParam('company', NumberParam);
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const users = useRecoilValue(userListFamily(my.permissions.has('users modifier:all') && companyId ? companyId : my.company.id));
    const {
        openAddUserModal,
        openEditUserModal,
        confirmDeleteUser,
        confirmSendVerification,
        openYardSelectModal,
        openVesselSelectModal,
    } = useUserModals(companyId || my.company.id);

    if (!users.length)
        return null;

    const company = users[0].company;

    const addButton = <>
        {my.permissions.has('users permissions') && (
            <Button
                size="small"
                component={Link}
                href={'/users/permissions'}
            >
                {t('permissions')}
            </Button>
        )}

        {my.permissions.has('users create') && company.type !== CompanyType.Party ? (
            <IconButton
                aria-label="add user"
                onClick={() => openAddUserModal(company)}
            >
                <Add/>
            </IconButton>
        ) : null}
    </>;

    const editButton = (user: UserModel) => (
        <GridActionsCellItem
            icon={<Edit/>}
            label={t('edit')}
            onClick={() => openEditUserModal(user)}
        />
    );

    const setYardsButton = (user: UserModel) => (
        <GridActionsCellItem
            icon={<EmojiTransportation/>}
            label={t('set_yards')}
            onClick={() => openYardSelectModal(user)}
            showInMenu
        />
    );

    const setVesselsButton = (user: UserModel) => (
        <GridActionsCellItem
            icon={<DirectionsBoat/>}
            label={t('vessels')}
            onClick={() => openVesselSelectModal(user)}
            showInMenu
        />
    );

    const deleteButton = (user: UserModel) => (
        <GridActionsCellItem
            icon={<Delete/>}
            label={t('delete')}
            onClick={() => confirmDeleteUser(user)}
            showInMenu
        />
    );

    const columns: GridColDef<UserModel>[] = [
        {
            field: 'lastName',
            headerName: t('name'),
            minWidth: 200,
            flex: 1,
            valueGetter: params => `${params.row.lastName} ${params.row.firstName}`,
        },
        {
            field: 'email',
            headerName: t('email'),
            renderCell: params => <>
                <Link component="a" href={`mailto:${params.value}`}>{params.value}</Link>
                {!params.row.emailVerified && (
                    <Tooltip
                        title={
                            <Box sx={{textAlign: 'center'}}>
                                <Trans i18nKey="email_not_verified" t={t} components={{1: <br/>}}/>
                            </Box>
                        }
                        placement="top"
                    >
                        <WarningAmber
                            color="warning"
                            fontSize="small"
                            sx={{ml: 1}}
                            onClick={() => confirmSendVerification(params.row)}
                        />
                    </Tooltip>
                )}
            </>,
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'phone',
            headerName: t('phone'),
            ...typePhone,
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'role',
            headerName: t('roles'),
            flex: 1,
            minWidth: 160,
        },
        ...company.type === CompanyType.PortAgent ? [{
            field: 'workAtYards',
            headerName: t('yards'),
            flex: 1,
            minWidth: 100,
            valueFormatter: params => params.value?.map((yard: YardModel) => `${yard.port.name} ${yard.name}`).join(', ')
        }] as GridColDef<UserModel>[] : [],
        ...company.type === CompanyType.VesselOwner ? [{
            field: 'captainAtVessels',
            headerName: t('vessel'),
            flex: 1,
            minWidth: 100,
            valueFormatter: params => params.value?.map((vessel: VesselModel) => vessel.name).join(', ')
        }] as GridColDef<UserModel>[] : [],
        {
            field: 'actions',
            type: 'actions',
            getActions: params => {
                const act = [];
                if (my.permissions.has('users update')) act.push(editButton(params.row));
                if (my.permissions.has('users update') && company.type === CompanyType.PortAgent) act.push(setYardsButton(params.row));
                if (my.permissions.has('users update') && params.row.role === 'Captain') act.push(setVesselsButton(params.row));
                if (my.permissions.has('users delete')) act.push(deleteButton(params.row));
                return act;
            },
        },
    ];

    return (
        <Container maxWidth="xl" sx={{mt: 2}}>
            <Card>
                <CardHeader
                    title={t('users_of', {value: company.name})}
                    action={addButton}
                />
                <Box sx={{height: 'calc(100vh - 148px)', padding: '0 1rem'}}>
                    <DataGrid
                        rows={users}
                        columns={columns}
                    />
                </Box>
            </Card>
        </Container>
    )
};

const Users = () => {
    return (
        <Suspense fallback={null}>
            <UsersInner/>
        </Suspense>
    )
}

export default Users;
